/**
 * AdminLeakTraces — /admin/leak-traces
 *
 * Dashboard showing every watermarked-download trace (PDF or image) with
 * filters by kind, email, and date range. Also renders a "by user"
 * rollup so admins can instantly spot who's downloading suspiciously.
 *
 * Data sources:
 *   GET /api/admin/leak-traces           (list, filtered)
 *   GET /api/admin/leak-traces/by-user   (rollup)
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { toast } from 'sonner';
import LeakScanMap from '../../components/LeakScanMap';
import {
  ShieldAlert, FileText, Image as ImageIcon, RefreshCw, ExternalLink,
  Search, User, Copy, Check, Download, Globe, Eye, Ban, Plus, Trash2,
} from 'lucide-react';

const DAY_PRESETS = [
  { label: '24 h',  hours: 24 },
  { label: '7 d',   hours: 24 * 7 },
  { label: '30 d',  hours: 24 * 30 },
  { label: 'All',   hours: null },
];

const KIND_META = {
  pdf:   { icon: FileText,  color: 'text-red-700 dark:text-red-300' },
  image: { icon: ImageIcon, color: 'text-blue-700 dark:text-blue-300' },
};

const AdminLeakTraces = () => {
  const { API } = useContext(AuthContext);
  const [preset, setPreset] = useState(DAY_PRESETS[1]);       // 7 d default
  const [kind, setKind] = useState('');                        // '' = all
  const [emailFilter, setEmailFilter] = useState('');
  const [emailDebounced, setEmailDebounced] = useState('');
  const [traces, setTraces] = useState([]);
  const [totals, setTotals] = useState({ matching: 0, kinds: {} });
  const [users, setUsers] = useState([]);
  const [recentScans, setRecentScans] = useState([]);
  const [scanDomains, setScanDomains] = useState([]);
  const [scanDrilldown, setScanDrilldown] = useState(null); // { token, scans }
  const [blocklist, setBlocklist] = useState([]);
  const [newBlockPattern, setNewBlockPattern] = useState('');
  const [newBlockNote, setNewBlockNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [copiedToken, setCopiedToken] = useState(null);

  // Debounce email search so we don't spam the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setEmailDebounced(emailFilter.trim()), 300);
    return () => clearTimeout(t);
  }, [emailFilter]);

  const params = useCallback(() => {
    const p = {};
    if (kind) p.kind = kind;
    if (emailDebounced) p.email = emailDebounced;
    if (preset.hours) {
      const since = new Date(Date.now() - preset.hours * 60 * 60 * 1000);
      p.since = since.toISOString();
    }
    return p;
  }, [kind, emailDebounced, preset]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const p = params();
      const [listRes, byUserRes, scansRes, blockRes, domainsRes] = await Promise.all([
        axios.get(`${API}/admin/leak-traces`, { params: { ...p, limit: 200 } }),
        axios.get(`${API}/admin/leak-traces/by-user`, {
          params: { since: p.since, limit: 50 },
        }),
        axios.get(`${API}/admin/leak-scans/recent`, {
          params: { hours: preset.hours || 24 * 90, limit: 100 },
        }),
        axios.get(`${API}/admin/leak-blocklist`),
        axios.get(`${API}/admin/leak-scans/by-domain`, {
          params: { hours: preset.hours || 24 * 90 },
        }),
      ]);
      setTraces(listRes.data.traces || []);
      setTotals({
        matching: listRes.data.total_matching || 0,
        kinds:    listRes.data.counts_by_kind || {},
      });
      setUsers(byUserRes.data.users || []);
      setRecentScans(scansRes.data.scans || []);
      setBlocklist(blockRes.data.entries || []);
      setScanDomains(domainsRes.data.domains || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load leak traces.');
    } finally {
      setLoading(false);
    }
  }, [API, params, preset.hours]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openScanDrilldown = async (token) => {
    setScanDrilldown({ token, scans: null });
    try {
      const { data } = await axios.get(`${API}/admin/leak-traces/${token}/scans`);
      setScanDrilldown({ token, scans: data.scans || [] });
    } catch (err) {
      toast.error('Failed to load scans.');
    }
  };

  const downloadCsv = async () => {
    try {
      const p = params();
      const res = await axios.get(`${API}/admin/leak-traces/export.csv`, {
        params: p,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fda-leak-traces-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded.');
    } catch (err) {
      toast.error('Failed to export CSV.');
    }
  };

  const addBlockEntry = async () => {
    const pattern = newBlockPattern.trim();
    if (!pattern) { toast.error('Enter a hostname pattern.'); return; }
    try {
      await axios.post(`${API}/admin/leak-blocklist`, { pattern, note: newBlockNote });
      toast.success(`Added ${pattern} to blocklist.`);
      setNewBlockPattern('');
      setNewBlockNote('');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to add pattern.');
    }
  };

  const removeBlockEntry = async (id) => {
    try {
      await axios.delete(`${API}/admin/leak-blocklist/${id}`);
      toast.success('Blocklist entry removed.');
      fetchAll();
    } catch (err) {
      toast.error('Failed to remove entry.');
    }
  };

  const copy = (token) => {
    navigator.clipboard?.writeText(token).then(() => {
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 1200);
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="admin-leak-traces-page">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-primary" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Content protection</div>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground leading-tight mt-1">
              Leak-trace ledger
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Every watermarked PDF page + image carries a QR that resolves back to
              this ledger. Filter to spot heavy downloaders.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={downloadCsv} data-testid="download-csv-btn">
              <Download className="w-4 h-4 mr-2" /> Download CSV
            </Button>
            <Button variant="outline" onClick={fetchAll} data-testid="refresh-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4" data-testid="filter-bar">
          {/* Date range */}
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-xs text-muted-foreground mr-1">Window:</span>
            {DAY_PRESETS.map((p) => (
              <button
                key={p.label}
                onClick={() => setPreset(p)}
                className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                  preset.label === p.label
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-muted/40'
                }`}
                data-testid={`preset-${p.label.replace(' ', '')}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {/* Kind */}
          <div className="flex items-center flex-wrap gap-2">
            <span className="text-xs text-muted-foreground mr-1">Kind:</span>
            {[
              { value: '', label: 'All' },
              { value: 'pdf', label: 'PDF' },
              { value: 'image', label: 'Image' },
            ].map((k) => (
              <button
                key={k.value || 'all'}
                onClick={() => setKind(k.value)}
                className={`px-3 py-1 rounded-full text-xs font-bold border transition-colors ${
                  kind === k.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-foreground border-border hover:bg-muted/40'
                }`}
                data-testid={`kind-${k.value || 'all'}`}
              >
                {k.label}
              </button>
            ))}
          </div>
          {/* Email search */}
          <div className="relative">
            <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              placeholder="Filter by email"
              className="pl-9"
              data-testid="email-filter-input"
            />
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Total matching</div>
            <div className="text-2xl font-heading font-black text-foreground" data-testid="kpi-total">{totals.matching}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">PDFs</div>
            <div className="text-2xl font-heading font-black text-red-600" data-testid="kpi-pdf">{totals.kinds.pdf || 0}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Images</div>
            <div className="text-2xl font-heading font-black text-blue-600" data-testid="kpi-image">{totals.kinds.image || 0}</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Unique users</div>
            <div className="text-2xl font-heading font-black text-foreground" data-testid="kpi-users">{users.length}</div>
          </CardContent></Card>
        </div>

        <Tabs defaultValue="events" className="w-full">
          <TabsList>
            <TabsTrigger value="events" data-testid="tab-events">Events</TabsTrigger>
            <TabsTrigger value="by-user" data-testid="tab-by-user">By user</TabsTrigger>
            <TabsTrigger value="scans" data-testid="tab-scans">Where viewed</TabsTrigger>
            <TabsTrigger value="blocklist" data-testid="tab-blocklist">Blocklist</TabsTrigger>
          </TabsList>

          {/* -------------- Events list -------------- */}
          <TabsContent value="events">
            <Card>
              <CardHeader>
                <CardTitle>Recent downloads</CardTitle>
                <CardDescription>
                  Newest first · showing {traces.length} of {totals.matching}
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {loading && <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>}
                {!loading && traces.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground text-sm" data-testid="events-empty">
                    No leak traces match this filter.
                  </div>
                )}
                {!loading && traces.length > 0 && (
                  <div className="divide-y divide-border">
                    {traces.map((t) => {
                      const meta = KIND_META[t.kind] || KIND_META.pdf;
                      const Icon = meta.icon;
                      return (
                        <div key={t.id} className="px-4 py-3 flex items-start gap-3 hover:bg-muted/30 transition-colors" data-testid={`event-row-${t.token}`}>
                          <Icon className={`w-4 h-4 mt-0.5 ${meta.color} flex-shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-heading font-extrabold text-foreground truncate">{t.email}</span>
                              <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{t.kind}</Badge>
                              <button
                                onClick={() => copy(t.token)}
                                className="inline-flex items-center gap-1 text-[11px] font-mono text-muted-foreground hover:text-foreground"
                                data-testid={`copy-token-${t.token}`}
                              >
                                {copiedToken === t.token ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {t.token}
                              </button>
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                              <span>{new Date(t.created_at).toLocaleString()}</span>
                              <span>·</span>
                              <span>lesson {t.lesson_id || '—'}</span>
                              <span>·</span>
                              <span>ip {t.ip || '—'}</span>
                              {(t.scan_count > 0) && (
                                <>
                                  <span>·</span>
                                  <button
                                    onClick={() => openScanDrilldown(t.token)}
                                    className="inline-flex items-center gap-1 text-primary font-bold hover:underline"
                                    data-testid={`scans-${t.token}`}
                                  >
                                    <Eye className="w-3 h-3" />
                                    {t.scan_count} scan{t.scan_count === 1 ? '' : 's'}
                                  </button>
                                </>
                              )}
                            </div>
                            <div className="text-[10px] text-muted-foreground/80 mt-0.5 truncate font-mono">
                              {t.source_url}
                            </div>
                          </div>
                          <Link
                            to={`/leak/${t.token}`}
                            target="_blank"
                            rel="noopener"
                            className="text-[11px] font-bold text-primary hover:underline flex items-center gap-1 shrink-0"
                            data-testid={`resolve-${t.token}`}
                          >
                            Resolve <ExternalLink className="w-3 h-3" />
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* -------------- By-user rollup -------------- */}
          <TabsContent value="by-user">
            <Card>
              <CardHeader>
                <CardTitle>Top downloaders</CardTitle>
                <CardDescription>Sorted by total downloads in the selected window.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {loading && <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>}
                {!loading && users.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground text-sm" data-testid="users-empty">
                    No download activity in this window.
                  </div>
                )}
                {!loading && users.length > 0 && (
                  <div className="divide-y divide-border">
                    {users.map((u) => {
                      // Flag any user > 20 downloads OR > 3 unique IPs as suspicious.
                      const suspicious = u.total >= 20 || u.unique_ips >= 3;
                      return (
                        <div key={u.user_id} className="px-4 py-3 flex items-center gap-3" data-testid={`user-row-${u.user_id}`}>
                          {suspicious ? (
                            <Badge variant="destructive" className="text-[10px] uppercase tracking-widest">Flagged</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-[10px] uppercase tracking-widest">Normal</Badge>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-heading font-extrabold text-foreground truncate flex items-center gap-2">
                              <User className="w-3.5 h-3.5 text-muted-foreground" />
                              {u.email}
                            </div>
                            <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                              <span><strong>{u.total}</strong> total</span>
                              <span>·</span>
                              <span>{u.pdf_count} PDF</span>
                              <span>·</span>
                              <span>{u.image_count} img</span>
                              <span>·</span>
                              <span>{u.unique_lessons} lessons</span>
                              <span>·</span>
                              <span>{u.unique_ips} IPs</span>
                              <span>·</span>
                              <span>last {new Date(u.last_at).toLocaleString()}</span>
                            </div>
                          </div>
                          <Link
                            to={`/admin/users/${u.user_id}`}
                            className="text-[11px] font-bold text-primary hover:underline shrink-0"
                            data-testid={`user-manage-${u.user_id}`}
                          >
                            Manage →
                          </Link>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          {/* -------------- Where viewed (recent scans) -------------- */}
          <TabsContent value="scans">
            <div className="mb-4">
              <LeakScanMap scans={recentScans} height={360} />
            </div>

            {/* Domain-grouped rollup — at-a-glance risk assessment */}
            <Card className="mb-4" data-testid="domain-rollup-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Top leak-hosting domains
                </CardTitle>
                <CardDescription>
                  Referer hostnames rolled up by scan count. The bigger the
                  number, the more traffic that site is sending to your
                  watermarked copies.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {scanDomains.length === 0 && (
                  <div className="p-6 text-center text-muted-foreground text-sm" data-testid="domains-empty">
                    No scans with a referer in this window.
                  </div>
                )}
                {scanDomains.length > 0 && (
                  <div className="divide-y divide-border">
                    {scanDomains.slice(0, 15).map((d) => {
                      const alreadyBlocked = blocklist.some(
                        (b) => d.domain.includes(b.pattern.toLowerCase())
                      );
                      return (
                        <div
                          key={d.domain}
                          className="px-4 py-3 flex items-center gap-3"
                          data-testid={`domain-row-${d.domain}`}
                        >
                          {d.blocklist_hit || alreadyBlocked ? (
                            <Badge variant="destructive" className="text-[10px] uppercase tracking-widest">Blocked</Badge>
                          ) : (
                            <Badge variant="secondary" className="text-[10px] uppercase tracking-widest">Unblocked</Badge>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-heading font-extrabold text-foreground truncate font-mono">
                              {d.domain}
                            </div>
                            <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                              <span><strong>{d.count}</strong> scan{d.count === 1 ? '' : 's'}</span>
                              <span>·</span>
                              <span>{d.unique_users} licensee{d.unique_users === 1 ? '' : 's'} affected</span>
                              {d.countries.length > 0 && (
                                <>
                                  <span>·</span>
                                  <span>{d.countries.join(', ')}</span>
                                </>
                              )}
                              <span>·</span>
                              <span>last {new Date(d.last_at).toLocaleString()}</span>
                            </div>
                          </div>
                          {!alreadyBlocked && !d.blocklist_hit && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                try {
                                  await axios.post(`${API}/admin/leak-blocklist`, {
                                    pattern: d.domain,
                                    note: `Auto-added from domain rollup (${d.count} scans)`,
                                  });
                                  toast.success(`${d.domain} added to blocklist`);
                                  fetchAll();
                                } catch (err) {
                                  toast.error(err?.response?.data?.detail || 'Failed to add.');
                                }
                              }}
                              data-testid={`block-domain-${d.domain}`}
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" /> Block
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-4 h-4" /> Where your leaks are being viewed
                </CardTitle>
                <CardDescription>
                  Every time someone visits <code className="px-1 rounded bg-muted">/leak/&#123;token&#125;</code>{' '}
                  (either by scanning the QR on a leaked copy or by clicking a link), we log
                  their IP, user-agent, and (if the browser shares it) the referring page.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {loading && <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>}
                {!loading && recentScans.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground text-sm" data-testid="scans-empty">
                    No scans in this window. That&rsquo;s a good thing.
                  </div>
                )}
                {!loading && recentScans.length > 0 && (
                  <div className="divide-y divide-border">
                    {recentScans.map((s) => (
                      <div key={s.id} className="px-4 py-3 flex items-start gap-3" data-testid={`scan-row-${s.id}`}>
                        <Globe className="w-4 h-4 mt-0.5 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-heading font-extrabold text-foreground truncate">{s.email}</span>
                            <Badge variant="outline" className="text-[10px] uppercase tracking-widest">{s.kind}</Badge>
                            <button
                              onClick={() => copy(s.token)}
                              className="inline-flex items-center gap-1 text-[11px] font-mono text-muted-foreground hover:text-foreground"
                              data-testid={`scan-copy-${s.token}`}
                            >
                              {copiedToken === s.token ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />} {s.token}
                            </button>
                          </div>
                          <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                            <span>{new Date(s.created_at).toLocaleString()}</span>
                            <span>·</span>
                            <span>ip {s.ip || '—'}</span>
                            {s.geo?.country_code && (
                              <>
                                <span>·</span>
                                <span className="inline-flex items-center gap-1">
                                  <Globe className="w-3 h-3" />
                                  <strong>{s.geo.city ? `${s.geo.city}, ` : ''}{s.geo.country}</strong>
                                  <span className="text-muted-foreground">({s.geo.country_code})</span>
                                </span>
                                {s.geo.isp && (
                                  <>
                                    <span>·</span>
                                    <span>{s.geo.isp}</span>
                                  </>
                                )}
                              </>
                            )}
                            {s.blocklist_hit && (
                              <>
                                <span>·</span>
                                <Badge variant="destructive" className="text-[10px] uppercase" data-testid="blocklist-hit-badge">
                                  Blocklist: {s.blocklist_hit}
                                </Badge>
                              </>
                            )}
                            <span>·</span>
                            <span>{s.accept_language || '—'}</span>
                          </div>
                          <div className="text-[10px] text-muted-foreground/80 mt-0.5 font-mono break-all">
                            UA: {s.user_agent || '—'}
                          </div>
                          {s.referer && (
                            <div className="text-[11px] mt-1 font-mono break-all bg-red-50 dark:bg-red-950/30 border border-red-300/60 rounded px-2 py-1">
                              <span className="text-red-700 dark:text-red-300 font-bold">Referer:</span>{' '}
                              <a href={s.referer} target="_blank" rel="noopener noreferrer" className="underline text-red-800 dark:text-red-200">{s.referer}</a>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* -------------- Referer blocklist -------------- */}
          <TabsContent value="blocklist">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Ban className="w-4 h-4" /> Referer blocklist
                </CardTitle>
                <CardDescription>
                  When a QR scan comes from a hostname matching one of these
                  patterns (case-insensitive substring), the licensee is
                  auto-flagged on the Copy-Attempt monitor and admins get a
                  real-time DM.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Add form */}
                <div className="flex flex-col md:flex-row gap-2" data-testid="blocklist-add-form">
                  <Input
                    value={newBlockPattern}
                    onChange={(e) => setNewBlockPattern(e.target.value)}
                    placeholder="hostname pattern (e.g. leakforum.com)"
                    className="md:max-w-xs"
                    data-testid="block-pattern-input"
                  />
                  <Input
                    value={newBlockNote}
                    onChange={(e) => setNewBlockNote(e.target.value)}
                    placeholder="Optional note (why this domain?)"
                    className="flex-1"
                    data-testid="block-note-input"
                  />
                  <Button
                    onClick={addBlockEntry}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="block-add-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>

                {/* Existing entries */}
                <div className="divide-y divide-border border border-border rounded-lg">
                  {blocklist.length === 0 && (
                    <div className="p-6 text-sm text-muted-foreground text-center" data-testid="blocklist-empty">
                      No patterns yet. Add hostnames of known course-leak forums or Telegram groups.
                    </div>
                  )}
                  {blocklist.map((b) => (
                    <div key={b.id} className="px-4 py-3 flex items-center gap-3" data-testid={`block-row-${b.id}`}>
                      <Ban className="w-4 h-4 text-red-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-heading font-extrabold text-foreground font-mono">{b.pattern}</div>
                        {b.note && <div className="text-[11px] text-muted-foreground mt-0.5">{b.note}</div>}
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          Added {new Date(b.created_at).toLocaleDateString()} · by {b.added_by || 'unknown'}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeBlockEntry(b.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950/40"
                        data-testid={`block-remove-${b.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* -------------- Per-token scan drilldown modal -------------- */}
        {scanDrilldown && (
          <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setScanDrilldown(null)}
            data-testid="scan-drilldown-modal"
          >
            <div
              className="bg-background rounded-2xl border border-border max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-border">
                <h3 className="font-heading text-xl font-black">Scans for {scanDrilldown.token}</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {scanDrilldown.scans ? `${scanDrilldown.scans.length} scan${scanDrilldown.scans.length === 1 ? '' : 's'}` : 'Loading…'}
                </p>
              </div>
              <div className="p-4">
                {!scanDrilldown.scans && <div className="text-sm text-muted-foreground">Loading…</div>}
                {scanDrilldown.scans?.length === 0 && <div className="text-sm text-muted-foreground">No scans recorded yet.</div>}
                {scanDrilldown.scans?.length > 0 && (
                  <ul className="space-y-2 text-xs">
                    {scanDrilldown.scans.map((s) => (
                      <li key={s.id} className="border-b border-border pb-2 font-mono">
                        <div><span className="text-muted-foreground">at:</span> {new Date(s.created_at).toLocaleString()}</div>
                        <div><span className="text-muted-foreground">ip:</span> {s.ip || '—'}</div>
                        <div className="break-all"><span className="text-muted-foreground">ua:</span> {s.user_agent || '—'}</div>
                        {s.referer && (
                          <div className="break-all">
                            <span className="text-muted-foreground">ref:</span>{' '}
                            <a href={s.referer} target="_blank" rel="noopener noreferrer" className="underline text-primary">{s.referer}</a>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminLeakTraces;
